import React, { useEffect, useMemo } from 'react';

import { DeselectedMenuItemAction } from 'src/application/actions/menuActions';
import { useStudentDispatch } from 'src/application/hooks/useStudentDispatch';
import { useStudentSelector } from 'src/application/store/selectors/useStudentSelector';

import { ProjectViewModel } from '../../../../config/configs';
import { StudentViewMode } from '../../../../config/constants';
import { PATH_CONFIG } from '../../../../config/static/path.config';
import { convertToStoreForStudent } from '../../../../graphql/TourBuilderStorageClient';
import { Panorama } from '../../../../PanoramaLibrary/components/Panorama';
import imageUrl from '../../../../resources/arrow-tilted.svg';
import backgroundUrlTilted from '../../../../resources/wrapper-tilted.svg';
import backgroundUrl from '../../../../resources/wrapper.svg';
import { TourBuilderStoreIoCStudentFakeInstance } from '../../../../TourBuilderLibrary/domain/tourBuilderIoC';
import { createTourBuilderStore } from '../../../../TourBuilderLibrary/domain/tourBuilderStore';
import { TourBuilderStoreProvider } from '../../../../TourBuilderLibrary/domain/tourBuilderStoreContext';
import { createTranslationService } from '../../../../translates/services/translationService';
import { AudioElementContextProvider } from '../../../components/Audio/AudioElementContext';
import { DialogsPortal } from '../../../components/modal/DialogsPortal';
import { useOnMount } from '../../../hooks/useOnMount';
import { hotspotRendererFactory } from '../../../store/middleware/panorama/getHotspotRenderer';
import {
  IPersistenceTrackingClient,
  PersistenceTrackingData,
} from '../../../store/middleware/tracking/clients/trackingClient';
import { getCurrentItem } from '../../../store/selectors/getCurrentItem';
import { getStudentStore } from '../../../store/store';
import { CompletionOverlay } from '../../CompletionOverlay/CompletionOverlay';
import { DraftPanoramaPortal } from '../../EditItem/components/overlays/LocationSettings/DraftPanoramaPortal';
import { LinearProgressContextProvider } from '../../LinearProgress/LinearProgressContext';
import { ProgressDialog } from '../../LinearProgress/ProgressDialog';
import { StartingMessageModal } from '../../NavigationMenu/components/StartingMessageModal';
import { StudentMainMenu } from '../../NavigationMenu/components/student/StudentMainMenu';
import { NonSequencedFlow } from '../../NonSequencedFlow/components/NonSequencedFlow';
import { ResourceWrapper } from '../../Resource/ResourceWrapper';
import { SequencedContentFlowWrapper } from '../../SequencedFlow/components/SequencedContentFlowWrapper';
import { TestMeWrapper } from '../../TestMe/components/TestMeWrapper';
import { HomeOverview } from './HomeOverview/HomeOverview';
import { Meta } from './Meta';
import { isNonSequencedFlowItem } from '@ael/core-api';
import { convertTourSceneLinkDto, GLOBAL_PANO_DIV_ID, IPanorama, PanoramaV2 } from '@ael/core-api';
import { Toaster } from '@ael/shared-components-library';
import { VirtualTourIconsBundle } from '@ael/virtual-tour-icon-bundle';
import { RawIntlProvider } from 'react-intl';
import { Provider } from 'react-redux';

type StudentAppProps = {
  viewMode: StudentViewMode;
  backendUrl: string;
  projectViewModel: ProjectViewModel;
  onStudentReady: (panorama: IPanorama) => void;
  isInPreview: boolean;
  hideStudentHeader: boolean;
  persistenceTrackingClient: IPersistenceTrackingClient;
  persistedTrackingData: PersistenceTrackingData;
};

export const StudentApp = (props: StudentAppProps) => {
  const translationService = useMemo(
    () => createTranslationService(props.projectViewModel.settings.cultureCode),
    [props.projectViewModel.settings.cultureCode]
  );
  return (
    <RawIntlProvider value={translationService}>
      <Provider
        key="student"
        store={getStudentStore(
          props.projectViewModel,
          props.persistenceTrackingClient,
          props.persistedTrackingData,
          props.isInPreview
        )}
      >
        <Student {...props} />
      </Provider>
    </RawIntlProvider>
  );
};

const Student = (props: StudentAppProps) => {
  const currentItem = useStudentSelector(getCurrentItem);
  const currentResourceId = useStudentSelector((state) => state.currentResource);
  const tourBuilderStoreInitData = convertToStoreForStudent(props.projectViewModel.tour);
  const tourBuilderStore = createTourBuilderStore(TourBuilderStoreIoCStudentFakeInstance, tourBuilderStoreInitData);
  const dispatch = useStudentDispatch();
  const startingTourSceneGuid = props.projectViewModel.config.startingTourSceneGuid;
  useOnMount(() => {
    PanoramaV2.Create(
      props.projectViewModel.tour,
      'globalPanoramaId',
      PATH_CONFIG.SKIN_URL,
      hotspotRendererFactory(false),
      window.embedpano,
      startingTourSceneGuid ? { tourSceneGuid: startingTourSceneGuid, sceneIndex: 0 } : undefined,
      GLOBAL_PANO_DIV_ID
    ).then((panorama: IPanorama) => {
      props.projectViewModel.tour.tourScenes.forEach((t) => {
        t.links.forEach((l) => {
          const hotspot = convertTourSceneLinkDto(l, VirtualTourIconsBundle, {
            sceneLinkDefaultBackgroundUrlTilted: backgroundUrlTilted,
            sceneLinkDefaultBackgroundUrl: backgroundUrl,
            sceneLinkDefaultImageUrl: imageUrl,
          });
          hotspot.attach(panorama);
        });
      });

      props.onStudentReady(panorama);
    });
  });

  const [isHomeOverviewVisible, setIsHomeOverviewVisible] = React.useState(
    props.projectViewModel.config.homeOverview.useOverview
  );

  // Toggle home overview visibility based on content selection
  // Not great, but better than adding it to redux store...
  useEffect(() => {
    // Hide home overview when any item or resource is selected
    if (currentItem || currentResourceId) {
      setIsHomeOverviewVisible(false);
    }
  }, [currentItem, currentResourceId]);

  const handleShowHomeOverview = () => {
    setIsHomeOverviewVisible(true);
    if (currentItem) {
      dispatch(DeselectedMenuItemAction(currentItem));
    }
  };

  // In student view only show header for manager in preview
  return (
    <>
      <Meta
        projectName={props.projectViewModel.config.name}
        theme={props.projectViewModel.config.theme}
        froalaEditors={props.projectViewModel.settings.froalaEditors}
      />
      <TourBuilderStoreProvider store={tourBuilderStore}>
        <LinearProgressContextProvider isLinearModeOn={props.projectViewModel.config.isLinearModeEnabled}>
          <AudioElementContextProvider isAutoPlayOn={true}>
            <div className="editor">
              <div className={'student-content-wrapper'}>
                <Panorama targetId={GLOBAL_PANO_DIV_ID} />

                <div className="student-content-wrapper__center-pane">
                  <HomeOverview
                    projectViewModel={props.projectViewModel}
                    backendUrl={props.backendUrl}
                    hideStudentHeader={props.hideStudentHeader}
                    isInPreview={props.isInPreview}
                    viewMode={props.viewMode}
                    isVisible={isHomeOverviewVisible}
                  />

                  <StudentMainMenu
                    backendUrl={props.backendUrl}
                    viewMode={props.viewMode}
                    projectViewModel={props.projectViewModel}
                    hideStudentHeader={props.hideStudentHeader}
                    isInPreview={props.isInPreview}
                    onShowHomeOverview={handleShowHomeOverview}
                  />
                  <StartingMessageModal froalaEditorsSettings={props.projectViewModel.settings.froalaEditors} />
                  <SequencedContentFlowWrapper froalaEditorsSettings={props.projectViewModel.settings.froalaEditors} />
                  <ResourceWrapper froalaEditorsSettings={props.projectViewModel.settings.froalaEditors} />
                  <TestMeWrapper froalaEditorsSettings={props.projectViewModel.settings.froalaEditors} />
                  {currentItem && isNonSequencedFlowItem(currentItem) && (
                    <NonSequencedFlow
                      nonSequencedFlowItem={currentItem}
                      froalaEditorsSettings={props.projectViewModel.settings.froalaEditors}
                    />
                  )}
                  <ProgressDialog />
                </div>
                <DraftPanoramaPortal />
              </div>

              {/* For react portal and dialogs, do not remove */}
              <DialogsPortal />
              <CompletionOverlay
                viewMode={props.viewMode}
                projectName={props.projectViewModel.config.name}
                isInPreview={props.isInPreview}
                hideStudentHeader={props.hideStudentHeader}
              />
              <Toaster position="top-center" />
            </div>
          </AudioElementContextProvider>
        </LinearProgressContextProvider>
      </TourBuilderStoreProvider>
    </>
  );
};
